// frontend/src/components/documents/Anexo1Modal.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Paper,
  Divider,
  CircularProgress,
  IconButton
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Print as PrintIcon,
  Build as BuildIcon
} from '@mui/icons-material';

const Anexo1Modal = ({ open, onClose, clientId, client, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [technicians, setTechnicians] = useState([]);
  const [selectedTechnician, setSelectedTechnician] = useState('');
  
  // Equipos seleccionados
  const [equipments, setEquipments] = useState([
    { name: 'Router Wi-Fi Principal Dual Band', brand: '', model: '', serialNumber: '', macAddress: client?.mac_address || '', ownership: 'ISP', assignmentType: 'RENTAL', returnRequired: true, value: 120 }
  ]);

  // Accesorios y Materiales
  const [accessories, setAccessories] = useState({
    utpCable: false,
    rj45Connectors: false,
    mast: false,
    fixingKit: false,
    grounding: false,
    dropFiber: false,
    rosette: false
  });

  // Datos de prueba técnica
  const [signalDb, setSignalDb] = useState('-62 dBm');
  const [pingMs, setPingMs] = useState('14 ms');
  const [downloadSpeed, setDownloadSpeed] = useState(client?.plan_name ? `${client.download_speed || '30'} Mbps` : '30 Mbps');
  const [uploadSpeed, setUploadSpeed] = useState(client?.plan_name ? `${client.upload_speed || '10'} Mbps` : '10 Mbps');
  const [observations, setObservations] = useState('Instalación realizada conforme a las normas de seguridad técnica WISP.');

  useEffect(() => {
    if (open) {
      fetchTechnicians();
      fetchExistingClientEquipment();
    }
  }, [open, clientId]);

  const fetchTechnicians = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/technicians', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTechnicians(data);
        if (data.length > 0) setSelectedTechnician(data[0].id);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchExistingClientEquipment = async () => {
    if (!clientId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/client-equipment/client/${clientId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          const mapped = data.map(item => ({
            itemId: item.item_id || item.equipment_id || item.id,
            name: item.item_name || item.name || 'Equipo de Conexión',
            brand: item.brand || '',
            model: item.model || '',
            serialNumber: item.serial_number || '',
            macAddress: item.mac_address || '',
            ownership: item.ownership || 'ISP',
            assignmentType: (item.assignment_type || item.acquisition_type || 'RENTAL').toUpperCase(),
            returnRequired: item.return_required !== false,
            value: parseFloat(item.item_value || item.charged_amount || 150)
          }));
          setEquipments(mapped);
        }
      }
    } catch (err) {
      console.error('Error auto-loading existing client equipment:', err);
    }
  };

  const handleRemoveEquipmentRow = (index) => {
    if (equipments.length === 1) return;
    const updated = equipments.filter((_, idx) => idx !== index);
    setEquipments(updated);
  };

  const handleEquipmentChange = (index, field, value) => {
    const updated = [...equipments];
    updated[index][field] = value;
    if (field === 'assignmentType') {
      if (value === 'SALE') {
        updated[index].ownership = 'CLIENT';
        updated[index].returnRequired = false;
      } else if (value === 'CONSUMABLE') {
        updated[index].ownership = 'ISP';
        updated[index].returnRequired = false;
      } else if (value === 'RENTAL') {
        updated[index].ownership = 'ISP';
        updated[index].returnRequired = true;
      }
    }
    setEquipments(updated);
  };

  const handleAddEquipmentRow = () => {
    setEquipments([
      ...equipments,
      { name: 'Nuevo Equipo / Accesorios', brand: '', model: '', serialNumber: '', macAddress: '', ownership: 'ISP', assignmentType: 'RENTAL', returnRequired: true, value: 50 }
    ]);
  };

  const handleSubmit = async (formatToPrint = null) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        clientId,
        technicianId: selectedTechnician,
        equipments,
        accessories,
        technicalData: {
          technicianName: technicians.find(t => t.id === selectedTechnician)?.name || 'Técnico YinoWISP',
          installationDate: new Date().toLocaleDateString('es-PE'),
          signalDb,
          pingMs,
          downloadSpeed,
          uploadSpeed
        },
        serviceData: {
          planName: client?.plan_name || 'Plan 30 Mbps',
          price: client?.plan_price || '70.00',
          guaranteedSpeed: '21 Mbps'
        },
        observations
      };

      const res = await fetch('/api/documents/anexo1', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error guardando Ficha de Instalación');

      alert('✅ ¡Ficha de Instalación (Anexo 1) generada exitosamente!');
      
      if (formatToPrint && data.document) {
        window.open(`/api/documents/${data.document.id}/pdf?format=${formatToPrint}&token=${token}`, '_blank');
      }

      if (onSuccess) onSuccess();
    } catch (err) {
      alert('❌ Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 800 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BuildIcon color="primary" /> Generar Ficha de Instalación y Entrega (Anexo 1)
        </Box>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: '#f8fafc' }}>
        <Box sx={{ mb: 3, p: 2, bgcolor: 'white', borderRadius: '10px', border: '1px solid #cbd5e1' }}>
          <Typography variant="subtitle2" fontWeight="bold" color="primary" sx={{ mb: 1 }}>1. DATOS DEL CLIENTE Y TÉCNICO</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2"><strong>Abonado:</strong> {client?.first_name} {client?.last_name}</Typography>
              <Typography variant="body2"><strong>DNI / RUC:</strong> {client?.dni}</Typography>
              <Typography variant="body2"><strong>Dirección:</strong> {client?.address}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Técnico Responsable"
                value={selectedTechnician}
                onChange={(e) => setSelectedTechnician(e.target.value)}
              >
                {technicians.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.first_name || t.name} {t.last_name || ''}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Box>

        {/* Equipos Entregados */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'white', borderRadius: '10px', border: '1px solid #cbd5e1' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold" color="primary">2. EQUIPOS ENTREGADOS Y MODALIDAD</Typography>
            <Button size="small" onClick={handleAddEquipmentRow}>+ Agregar Fila</Button>
          </Box>

          {equipments.map((eq, idx) => (
            <Paper key={idx} variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: '#fafafa' }}>
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <TextField fullWidth size="small" label="Equipo" value={eq.name || ''} onChange={(e) => handleEquipmentChange(idx, 'name', e.target.value)} />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField fullWidth size="small" label="Marca" value={eq.brand || ''} onChange={(e) => handleEquipmentChange(idx, 'brand', e.target.value)} />
                </Grid>
                <Grid item xs={6} sm={2.5}>
                  <TextField fullWidth size="small" label="Modelo" value={eq.model || ''} onChange={(e) => handleEquipmentChange(idx, 'model', e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={4.5}>
                  <TextField fullWidth size="small" label="Serie / MAC" value={eq.macAddress !== undefined && eq.macAddress !== null && eq.macAddress !== '' ? eq.macAddress : (eq.serialNumber || '')} onChange={(e) => { handleEquipmentChange(idx, 'macAddress', e.target.value); handleEquipmentChange(idx, 'serialNumber', e.target.value); }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField select fullWidth size="small" label="Modalidad" value={eq.assignmentType || 'RENTAL'} onChange={(e) => handleEquipmentChange(idx, 'assignmentType', e.target.value)}>
                    <MenuItem value="RENTAL">ALQUILER (ISP - Devuelve: SÍ)</MenuItem>
                    <MenuItem value="SALE">VENTA / COMPRA (Cliente - Devuelve: NO)</MenuItem>
                    <MenuItem value="CONSUMABLE">CONSUMIBLE (Insumo - Devuelve: NO)</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="Valor Reposición (S/)" type="number" value={eq.value !== undefined ? eq.value : ''} onChange={(e) => handleEquipmentChange(idx, 'value', e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color={eq.returnRequired ? 'error' : 'textSecondary'} fontWeight={700}>
                    {eq.returnRequired ? '⚠️ Requiere Devolución' : '✓ Sin Devolución'}
                  </Typography>
                  {equipments.length > 1 && (
                    <Button size="small" color="error" onClick={() => handleRemoveEquipmentRow(idx)}>
                      Eliminar
                    </Button>
                  )}
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Box>

        {/* Accesorios y Materiales */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'white', borderRadius: '10px', border: '1px solid #cbd5e1' }}>
          <Typography variant="subtitle2" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
            3. ACCESORIOS Y MATERIALES COMPLEMENTARIOS
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={<Checkbox checked={accessories.utpCable} onChange={(e) => setAccessories({ ...accessories, utpCable: e.target.checked })} size="small" />}
                label={<Typography variant="body2">Cable UTP Categ 5e/6</Typography>}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={<Checkbox checked={accessories.rj45Connectors} onChange={(e) => setAccessories({ ...accessories, rj45Connectors: e.target.checked })} size="small" />}
                label={<Typography variant="body2">Conectores RJ45 Blindados</Typography>}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={<Checkbox checked={accessories.mast} onChange={(e) => setAccessories({ ...accessories, mast: e.target.checked })} size="small" />}
                label={<Typography variant="body2">Mástil Galvanizado</Typography>}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={<Checkbox checked={accessories.fixingKit} onChange={(e) => setAccessories({ ...accessories, fixingKit: e.target.checked })} size="small" />}
                label={<Typography variant="body2">Kit Pernos / Tarugos</Typography>}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={<Checkbox checked={accessories.grounding} onChange={(e) => setAccessories({ ...accessories, grounding: e.target.checked })} size="small" />}
                label={<Typography variant="body2">Toma a Tierra</Typography>}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={<Checkbox checked={accessories.dropFiber} onChange={(e) => setAccessories({ ...accessories, dropFiber: e.target.checked })} size="small" />}
                label={<Typography variant="body2">Cable Drop Fibra Óptica</Typography>}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={<Checkbox checked={accessories.rosette} onChange={(e) => setAccessories({ ...accessories, rosette: e.target.checked })} size="small" />}
                label={<Typography variant="body2">Roseta / Caja NAP</Typography>}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Pruebas Técnicas */}
        <Box sx={{ p: 2, bgcolor: 'white', borderRadius: '10px', border: '1px solid #cbd5e1' }}>
          <Typography variant="subtitle2" fontWeight="bold" color="primary" sx={{ mb: 1.5 }}>4. PARÁMETROS TÉCNICOS DE PRUEBA EN VIVO</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth size="small" label="Nivel Señal dBm" value={signalDb} onChange={(e) => setSignalDb(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth size="small" label="Ping Latencia" value={pingMs} onChange={(e) => setPingMs(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth size="small" label="Test Descarga" value={downloadSpeed} onChange={(e) => setDownloadSpeed(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth size="small" label="Test Subida" value={uploadSpeed} onChange={(e) => setUploadSpeed(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} size="small" label="Observaciones de Instalación" value={observations} onChange={(e) => setObservations(e.target.value)} />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: '#f1f5f9', display: 'flex', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" color="primary" startIcon={<PrintIcon />} onClick={() => handleSubmit('A4')} disabled={loading}>
            Guardar e Imprimir A4
          </Button>
          <Button variant="outlined" color="info" startIcon={<PrintIcon />} onClick={() => handleSubmit('A5')} disabled={loading}>
            Imprimir Talonario A5
          </Button>
        </Box>
        <Button onClick={onClose} color="inherit">Cancelar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default Anexo1Modal;
